import { Device, ForecastSlice, MarketLocation } from '@e2m-customer-portal/hooks';
import { isBiogas, isBiomass } from '@e2m-customer-portal/shared';
import { ExpandIcon, FormattedValue } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import clsx from 'clsx';
import { eachDayOfInterval, getTime, Interval } from 'date-fns';
import { ForecastProperty, ForecastTarget } from 'generated/graphql';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { OptimizationStatus } from './OptimizationStatus';
import { useProductionReduction } from './utils';

export interface DeviceTableHeaderProps {
  open?: boolean;
  marketLocation: MarketLocation;
  device: Device;
  interval: Interval;
  slices: readonly ForecastSlice[];
  deviceMap?: (HTMLInputElement | null)[][];
  disableAssistant?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  currentForecastProperty?: ForecastProperty;
  isFifteenMinutesResolution?: boolean;
}

export function DeviceTableHeader({
  open = false,
  marketLocation,
  device,
  interval,
  slices,
  deviceMap,
  disabled,
  onClick,
  currentForecastProperty,
  isFifteenMinutesResolution,
}: Readonly<DeviceTableHeaderProps>): ReactElement {
  const [t] = useTranslation();
  const days = eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end));
  const { getSum, getAverage } = useProductionReduction(slices, days, isFifteenMinutesResolution, deviceMap, {
    shouldUseLiveStats: open && disabled,
    slicesAreSortedByHour: true,
  });

  return (
    <div className="mt-12 bg-grey-50 pb-1">
      <div className="device-table-header border-divider border-0 border-y border-solid bg-white first-of-type:border-t-0">
        <div className="flex flex-wrap items-center p-2 text-left xl:flex-nowrap xl:py-1">
          <div className="w-full flex justify-start gap-2">
            <div className="flex w-max items-center">
              <Button
                className="px-6 bg-grey-50"
                name={device.name}
                endIcon={
                  <ExpandIcon className={clsx('mr-3 leading-none', { 'fill-grey-300': disabled })} open={open} />
                }
                onClick={onClick}
              />
            </div>
            <div
              data-testid="device-header"
              className="hidden w-fit flex-nowrap justify-start pl-1 pr-4 sm:flex gap-6 xl:w-full"
            >
              <div className="border-grey-300 min-w-36 flex flex-col border-0 border-l border-solid pl-2">
                <span className="text-grey-400 text-xs">{t('MARKET_LOCATION_FORECAST_SUM')}</span>
                <span className="sm:text-xl">
                  <div className="flex items-center">
                    <FormattedValue
                      variant="raw"
                      value={getSum().value !== null ? getSum() : t('SHARED_UNKNOWN')}
                      fractionDigits={0}
                    />
                  </div>
                </span>
              </div>
              <div className="border-grey-300 flex flex-col -ml-1  border-0 border-l border-solid pl-2">
                <span className="text-grey-400 text-xs">{t('MARKET_LOCATION_FORECAST_AVG')}</span>
                <span className="sm:text-xl">
                  <div className="flex items-center">
                    <FormattedValue
                      variant="raw"
                      value={getAverage().value !== null ? getAverage() : t('SHARED_UNKNOWN')}
                      fractionDigits={0}
                    />
                  </div>
                </span>
              </div>
              {(isBiogas(marketLocation.technologies) || isBiomass(marketLocation.technologies)) &&
                currentForecastProperty?.target !== ForecastTarget.VPP && <OptimizationStatus device={device} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
